import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component,
  inject, signal, ViewChild
} from '@angular/core';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Select, SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { AutoValidateDirective } from '../../directives/auto-validate.directive';
import { FIELD_CONTROL, FIELD_CONFIG } from './field-tokens';
import { EmployeeCellComponent } from '../../components/employee-cell/employee-cell.component';
import { AppAvatarComponent } from '../../components/app-avatar/app-avatar.component';

@Component({
  selector: 'app-field-select',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule, ReactiveFormsModule,
    SelectModule, InputTextModule, ButtonModule, AutoValidateDirective,
    EmployeeCellComponent, AppAvatarComponent
  ],
  template: `
    @if (isAddingInline()) {
      <!-- Inline input mode (inline-input footerType) -->
      <div class="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-2 w-full">
        <input pInputText [value]="draftName()"
          (input)="draftName.set($any($event.target).value)"
          placeholder="Enter name" class="w-full md:min-w-[18rem]"
          (keydown.enter)="commitInline()" (keydown.esc)="cancelInline()" />
        <p-button label="Add" icon="pi pi-check" severity="primary" [outlined]="true" size="small"
          [disabled]="!draftName().trim()" (onClick)="commitInline()" />
        <p-button label="Back" icon="pi pi-list" [outlined]="true" severity="secondary" size="small"
          (onClick)="cancelInline()" />
      </div>
    } @else {
      <p-select #selectRef
        [formControl]="formControl"
        [options]="config.options ? config.options() : []"
        optionLabel="label"
        optionValue="value"
        appendTo="body"
        [placeholder]="config.placeholder || 'Select'"
        [class]="config.controlClass || 'w-full'"
        [filter]="config.filter || false"
        [filterBy]="config.filterBy || 'label'"
        [showClear]="config.showClear !== false"
        appAutoValidate>

        <ng-template #item let-opt>
          @if (opt.person) {
            <app-employee-cell [name]="opt.label" [sub]="opt.sub || ''" [avatar]="opt.avatar || ''" size="sm" />
          } @else {
            <span>{{ opt.label }}</span>
          }
        </ng-template>

        <ng-template #selectedItem let-opt>
          @if (opt.person) {
            <div class="flex items-center gap-2 min-w-0">
              <app-avatar [name]="opt.label" [src]="opt.avatar" size="xs" />
              <span class="truncate">{{ opt.label }}</span>
            </div>
          } @else {
            <span>{{ opt.label }}</span>
          }
        </ng-template>

        @if (config.dropdownIcon) {
          <ng-template pTemplate="dropdownicon">
            <i [class]="config.dropdownIcon"></i>
          </ng-template>
        }

        <ng-template pTemplate="header">
          @if (config.header) {
            <div class="font-medium p-3 text-sm text-gray-400">{{ config.header }}</div>
          }
        </ng-template>

        <ng-template pTemplate="footer">
          @if (config.action) {
            <div class="p-2 border-t border-gray-100 dark:border-gray-800">
              @if (config.action.footerType === 'dropdown-footer') {
                @if (!isAddingInDropdown()) {
                  <p-button
                    [label]="config.action.label"
                    [icon]="config.action.icon || 'pi pi-plus'"
                    [text]="true" [fluid]="true" severity="primary" size="small"
                    (onClick)="$event.stopPropagation(); isAddingInDropdown.set(true)" />
                } @else {
                  <div class="flex items-center gap-2 p-1" (click)="$event.stopPropagation()">
                    <input pInputText #newValInput
                      [placeholder]="config.placeholder || 'Enter name...'"
                      class="p-inputtext-sm flex-1"
                      (keydown.enter)="commitDropdownAdd(newValInput.value)"
                      (keydown.esc)="isAddingInDropdown.set(false)" />
                    <p-button icon="pi pi-check" size="small"
                      (onClick)="commitDropdownAdd(newValInput.value)" />
                    <p-button icon="pi pi-times" [text]="true" severity="secondary" size="small"
                      (onClick)="isAddingInDropdown.set(false)" />
                  </div>
                }
              } @else {
                <p-button
                  [label]="config.action.label"
                  [icon]="config.action.icon || 'pi pi-plus'"
                  [text]="config.action.text ?? true"
                  [fluid]="config.action.fluid ?? true"
                  [severity]="config.action.severity || 'primary'"
                  size="small"
                  (onClick)="config.action.footerType === 'inline-input'
                    ? startInline()
                    : config.action.command?.()">
                </p-button>
              }
            </div>
          }
        </ng-template>

      </p-select>
    }
  `
})
export class SelectFieldComponent {
  readonly config  = inject(FIELD_CONFIG);
  readonly control = inject(FIELD_CONTROL);
  private readonly cdr = inject(ChangeDetectorRef);

  @ViewChild('selectRef') private selectRef?: Select;

  get formControl(): FormControl { return this.control as FormControl; }

  // Inline-input state (replaces the whole select with an input row)
  isAddingInline    = signal(false);
  draftName         = signal('');

  // Dropdown-footer inline state (inside the dropdown panel)
  isAddingInDropdown = signal(false);

  startInline(): void {
    this.isAddingInline.set(true);
    this.draftName.set('');
  }

  cancelInline(): void {
    this.isAddingInline.set(false);
    this.draftName.set('');
  }

  commitInline(): void {
    const name = this.draftName().trim();
    if (!name || !this.config.action?.onCommit) return;
    this.config.action.onCommit(name);
    this.cancelInline();
  }

  commitDropdownAdd(value: string): void {
    const name = value?.trim();
    if (!name || !this.config.action?.onCommit) return;

    const valueBefore = this.formControl.value;
    this.selectRef?.hide();
    this.config.action.onCommit(name);
    this.isAddingInDropdown.set(false);

    this.cdr.detectChanges();

    const currentOptions = this.config.options ? this.config.options() : [];
    const newOption = currentOptions[currentOptions.length - 1];
    const newValue  = newOption?.value;
    const ctrl      = this.formControl;

    if (ctrl) {
      const currentValue = ctrl.value;
      const targetValue  = currentValue !== valueBefore ? currentValue : newValue;
      if (targetValue != null) {
        if (this.selectRef) this.selectRef.options = currentOptions;
        ctrl.setValue(null, { emitEvent: false });
        ctrl.setValue(targetValue);
        this.cdr.detectChanges();
      }
    }
  }
}
